import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { getDatabase, ref, push, set } from 'firebase/database';
import './Registration.css';
import { FaSpinner } from 'react-icons/fa';
import ImageKitUpload from './ImageKitUpload';
import './ImageKitUpload.css';
import { nigerianStates } from '../data/locationData';
import { sessions, classes, studentStatus as studentStatusOptions } from '../data/schoolData';

const Signup = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [fullName, setFullName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [address, setAddress] = useState('');
    const [role, setRole] = useState('student');
    const [profilePicUrl, setProfilePicUrl] = useState('');
    const [session, setSession] = useState('');
    const [className, setClassName] = useState('');
    const [studentStatus, setStudentStatus] = useState('');
    const [state, setState] = useState('');
    const [lga, setLga] = useState('');

    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const onUploadSuccess = (res) => {
        setProfilePicUrl(res.url);
    };

    const onUploadError = (err) => {
        setError('Failed to upload profile picture. Please try again.');
        console.error('Upload error:', err);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess(false);

        if (!profilePicUrl) {
            setError('Please upload a profile picture.');
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters long.');
            return;
        }

        setLoading(true);

        try {
            const db = getDatabase();

            const pendingUserData = {
                fullName,
                email,
                phoneNumber,
                address,
                role,
                profilePicUrl,
                password,
                session,
                className,
                studentStatus,
                state,
                lga,
                status: 'pending',
                submittedAt: new Date().toISOString(),
            };

            const pendingUsersRef = ref(db, 'pending_users');
            const newUserRef = push(pendingUsersRef);
            await set(newUserRef, pendingUserData);

            setSuccess(true);

        } catch (err) {
            setError(`Failed to submit application: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="registration-container">
                <h2>Application Submitted!</h2>
                <p className="form-text" style={{ color: '#28a745', fontSize: '1.1rem' }}>
                    Thank you! Your registration is pending approval.
                    You will be notified via email once an administrator activates your account.
                </p>
                <div className="form-text">
                    <Link to="/login">Back to Login</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="registration-container">
            <h2>Create an Account</h2>
            {error && <p className="alert alert-danger">{error}</p>}
            <form onSubmit={handleSubmit}>
                <div className="profile-pic-upload-group">
                    <label>Upload Profile Picture</label>
                    <ImageKitUpload
                        onUploadSuccess={onUploadSuccess}
                        onUploadError={onUploadError}
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="fullName">Full Name</label>
                    <input id="fullName" type="text" className="form-control" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
                </div>

                <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <input id="email" type="email" className="form-control" value={email} onChange={(e) => setEmail(e.target.value)} required />
                </div>

                <div className="form-group">
                    <label htmlFor="password">Password</label>
                    <input id="password" type="password" className="form-control" value={password} onChange={(e) => setPassword(e.target.value)} required />
                </div>

                <div className="form-group">
                    <label htmlFor="phoneNumber">Phone Number</label>
                    <input id="phoneNumber" type="tel" className="form-control" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} required />
                </div>

                <div className="form-group">
                    <label htmlFor="address">Home Address</label>
                    <input id="address" type="text" className="form-control" value={address} onChange={(e) => setAddress(e.target.value)} required />
                </div>

                <div className="form-group">
                    <label htmlFor="role">I am a...</label>
                    <select id="role" className="form-select" value={role} onChange={(e) => setRole(e.target.value)}>
                        <option value="student">Student</option>
                        <option value="parent">Parent</option>
                        <option value="staff">Staff Member</option>
                    </select>
                </div>

                {role === 'student' && (
                    <>
                        <div className="form-group">
                            <label htmlFor="session">Session</label>
                            <select id="session" className="form-select" value={session} onChange={(e) => setSession(e.target.value)} required>
                                <option value="">Select Session</option>
                                {sessions.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>

                        <div className="form-group">
                            <label htmlFor="className">Class</label>
                            <select id="className" className="form-select" value={className} onChange={(e) => setClassName(e.target.value)} required>
                                <option value="">Select Class</option>
                                {classes.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>

                        <div className="form-group">
                            <label htmlFor="studentStatus">Status</label>
                            <select id="studentStatus" className="form-select" value={studentStatus} onChange={(e) => setStudentStatus(e.target.value)} required>
                                <option value="">Select Status</option>
                                {studentStatusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>

                        <div className="form-group">
                            <label htmlFor="state">State</label>
                            <select id="state" className="form-select" value={state} onChange={(e) => setState(e.target.value)} required>
                                <option value="">Select State</option>
                                {nigerianStates.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
                            </select>
                        </div>

                        <div className="form-group">
                            <label htmlFor="lga">LGA</label>
                            <select id="lga" className="form-select" value={lga} onChange={(e) => setLga(e.target.value)} required>
                                <option value="">Select LGA</option>
                                {state && nigerianStates.find(s => s.name === state)?.lgas.map(l => <option key={l} value={l}>{l}</option>)}
                            </select>
                        </div>
                    </>
                )}

                <button type="submit" className="btn-submit" disabled={loading}>
                    {loading ? <><FaSpinner className="spinner"/> Submitting...</> : 'Sign Up for Approval'}
                </button>
            </form>
            <p className="form-text">
                Already have an account? <Link to="/login">Log In</Link>
            </p>
        </div>
    );
};

export default Signup;
